import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

import { CoreQuestionsFactoryService, FormQuestionsService } from 'core/services';
import { QuestionBase, QuestionGroup, SwitchQuestion, Decorator, RangeQuestion } from 'core/components/forms';
import { UntypedFormGroup } from '@angular/forms';

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

@Injectable()
export class GroupConfigScheduleQuestionsService implements FormQuestionsService {
  protected questions: QuestionBase<any>[];
  protected questionsSubject = new BehaviorSubject(null);
  protected formData: any = {};
  protected form: UntypedFormGroup;

  constructor(protected factory: CoreQuestionsFactoryService) {}

  setForm(form: UntypedFormGroup) {
    this.form = form;
  }

  getFormData(): any {
    return WEEKDAYS.reduce((schedule, weekday) => {
      const dayConfig = this.formData[weekday];
      schedule[weekday] = {
        allowed: dayConfig.allowed,
        start: dayConfig.timerange[0],
        finish: dayConfig.timerange[1]
      };
      return schedule;
    }, {});
  }

  onDataChanged(newData: any = {}) {
    this.formData = newData;
  }

  getQuestionsObservable() {
    return this.questionsSubject.asObservable();
  }

  getFormQuestions(params: any = {}): Observable<QuestionBase<any>[]> {
    this.questions = [];
    this.onDataChanged();
    this.createQuestions(params);
    return this.getQuestionsObservable();
  }

  createQuestions(params: any = {}) {
    this.questions = WEEKDAYS.map((weekday) => this.getWeekdayQuestion(params, weekday));
    this.questionsSubject.next(this.questions);
  }

  getWeekdayQuestion(params, weekday) {
    const { group } = params;
    const hoursRestrictions = group.hoursRestrictions || {};
    const weekdayConfig = hoursRestrictions[weekday] || {};
    const start = weekdayConfig.start || '00:00';
    const end = weekdayConfig.finish || '24:00';
    const rangeQuestion = new RangeQuestion({
      key: 'timerange',
      class: 'col-1',
      value: [start, end],
      config: {
        type: 'double',
        keyboard: true,
        grid: true,
        min: 0,
        max: 24 * 60,
        min_interval: 60,
        block: !weekdayConfig.allowed,
        step: weekdayConfig.allowed ? 30 : 0,
        from: weekdayConfig.allowed ? this.toMinutes(start) : 0,
        to: weekdayConfig.allowed ? this.toMinutes(end) : 0,
        hide_from_to: !weekdayConfig.allowed,
        prettify: (num) =>
          `${Math.floor(num / 60)
            .toString()
            .padStart(2, '0')}:${(num % 60).toString().padStart(2, '0')}`
      }
    });
    return new QuestionGroup({
      key: weekday,
      isSection: true,
      childQuestions: [
        new Decorator({
          label: `nouns.days.${weekday}`,
          class: 'col-1'
        }),
        new SwitchQuestion({
          key: 'allowed',
          class: 'col-1',
          switchClass: 'open-close text-centered',
          switchOffLabel: 'groups.config.schedule.open',
          switchOnLabel: 'groups.config.schedule.allowed',
          value: !!weekdayConfig.allowed,
          eventHandlers: {
            onChange: (value) => {
              const checked = value;
              const options = {
                block: !checked,
                step: checked ? 30 : 0,
                from: 0,
                to: checked ? 24 * 60 : 0,
                hide_from_to: !checked
              };
              rangeQuestion.options.config.slider.update(options);
              (this.form.controls[weekday] as UntypedFormGroup).controls['timerange'].setValue(['00:00', '24:00']);
            }
          }
        }),
        rangeQuestion
      ]
    });
  }

  toMinutes(time: string): number {
    const hhmm = time.split(':');
    return parseInt(hhmm[0], null) * 60 + parseInt(hhmm[1], null);
  }
}
